import { lazy } from "react";
import ProtectedRoute from "@/components/layout/protectedRoute.tsx";
import ErrorBoundary from "@/components/pages/error-page";
import { CompanyProvider } from "@/components/contexts/CompanyProvider";
import { UserRoleEnum } from "@t/UserRoleEnum";
import { UserPermissionsEnum } from "@t/UserPermissionsEnum";
import { createBrowserRouter, RouteObject } from "react-router-dom";
import * as Sentry from "@sentry/react";

// Layouts — lazy so each layout's transitive deps only load when that section is visited
const BaseLayout = lazy(() => import("@/components/layout/layout"));
const D2DLayout = lazy(() => import("@/components/layout/d2d-layout"));

const OAuthConsentPage = lazy(() => import("@/components/pages/public/oauth-consent-page"));

// Public / auth pages
const LoginPage = lazy(() => import("@/components/pages/public/login-page"));
const MaintenancePage = lazy(() => import("@/components/pages/public/maintenance-page"));
const ForgotPasswordPage = lazy(() => import("@/components/pages/public/forgot-password-page"));
const ResetPasswordPage = lazy(() => import("@/components/pages/public/reset-password-page"));
const MfaSetupPage = lazy(() => import("@/components/pages/mfa/setup-mfa"));
const MfaVerificationPage = lazy(() => import("@/components/pages/mfa/verify-mfa"));

// D2D pages — maps/signalr deps only pulled when /d2d or /door-knocking-map is visited
const DoorKnockingMap = lazy(() => import("@/components/pages/doorknocking-map/door-knocking-map"));
const TargetSaleScheduling = lazy(
    () => import("@/components/viewers/d2d/target-sale/target-sale-scheduling")
);

// Core app pages
const HomePage = lazy(() => import("@/components/pages/home/home-page"));
const TaskPage = lazy(() => import("./components/pages/tasks/tasks-page"));
const CustomerPage = lazy(() => import("@/components/pages/customers/customers-page"));
const LeadsPage = lazy(() => import("@/components/pages/leads/leads-page"));
const CommercialLeadViewerPage = lazy(
    () => import("@/components/pages/commercial-leads/commercial-lead-viewer-page")
);
const CommercialContractCreatePage = lazy(
    () => import("@/components/pages/commercial-leads/commercial-contract-create-page")
);
const CommercialContractSentPage = lazy(
    () => import("@/components/pages/commercial-leads/commercial-contract-sent-page")
);
const MapAreaPage = lazy(() => import("@/components/pages/area-map/map-area-page"));
const CollectionsPage = lazy(() => import("@/components/pages/collections/collections-page"));
const CallLogsPage = lazy(() => import("@/components/pages/call-logs/call-logs-page"));
const CommunicationsPage = lazy(
    () => import("@/components/pages/communications/communications-page")
);
const ReferralPage = lazy(() => import("@/components/pages/referral/referral-page"));
const UserActivityPage = lazy(() => import("@/components/pages/user-activity/user-activity-page"));
const AccountPage = lazy(() => import("@/components/pages/account/account-page"));
const WorkOrderHtmlPage = lazy(() => import("@/components/pages/work-order/work-order-html-page"));
const SchedulingPage = lazy(() => import("@/components/pages/scheduling/scheduling-board-page"));
const EmployeeContractCreatePage = lazy(
    () => import("@/components/pages/employee-contract/employee-contract-create-page.tsx")
);
const EmployeeContractPage = lazy(
    () => import("@/components/pages/employee-contract/employee-contract-page")
);

// Admin pages
const AdminPage = lazy(() => import("@/components/pages/admin/admin-page"));
const BulkFieldRoutesUsersPage = lazy(
    () => import("@/components/pages/admin/bulk-fieldroutes-users/bulk-fieldroutes-users-page")
);
const KPIDashboardPage = lazy(() => import("./components/pages/kpi-page"));

// Dashboard pages — recharts only loaded when a dashboard is visited
const MarketingReporting = lazy(
    () => import("@/components/pages/dashboards/marketing/marketing-reporting-dashboard")
);
const LeadsReportingPage = lazy(
    () => import("@/components/pages/dashboards/leads/leads-reporting-dashboard")
);
const DoorknockingDashboard = lazy(
    () => import("@/components/pages/dashboards/doorknocking/doorknocking-dashboard")
);
const BranchManagerDashboard = lazy(
    () => import("@/components/pages/dashboards/branch-manager/branch-manager-dashboard")
);
const CommercialReportingDashboard = lazy(
    () => import("@/components/pages/dashboards/commercial/commercial-reporting-dashboard")
);
const StaleLeadsDashboard = lazy(
    () => import("@/components/pages/dashboards/stale-leads/stale-leads-dashboard")
);

// Contract / PDF / misc pages — @react-pdf/renderer only loaded when these are visited
const ContractsPageHtml = lazy(() => import("@/components/pages/contracts/contracts-page-html"));
const ContractsCommercialPageHtml = lazy(
    () => import("@/components/pages/contracts/contracts-commercial-page-html")
);
const DynamicContractPage = lazy(
    () => import("@/components/pages/dynamic-contract/dynamic-contract-page")
);
// import ProposalPDFPage from "@/components/pages/proposal/proposal-page"; // DEPRECATED: replaced by /proposal-html
const ProposalHtmlPage = lazy(() => import("@/components/pages/proposal/proposal-html-page"));
const CustomerReferralPage = lazy(
    () => import("@/components/pages/customer-referral/customer-referral-page")
);
const CommercialContractPage = lazy(
    () => import("@/components/pages/commercial-contract/commercial-contract-page")
);
const PropertyInspectionPage = lazy(
    () => import("@/components/pages/property-inspection/property-inspection-page")
);
const GraphingEditorPage = lazy(
    () => import("@/components/pages/property-inspection/graphing/graphing-editor")
);
const TestWebForms = lazy(() => import("@/components/pages/web-forms/web-form-test-page"));
const TechLeadPage = lazy(() => import("@/components/pages/tech-lead-form/tech-lead-page"));

// Help center — renders the markdown user-guide vault from /docs
const HelpPage = lazy(() => import("@/components/pages/help"));
const TestFlowPage = lazy(() => import("@/components/pages/test-flow/test-flow-page"));

let routes: RouteObject[] = [
    {
        path: "/login",
        element: <LoginPage />,
    },
    {
        path: "/maintenance",
        element: <MaintenancePage />,
    },
    {
        path: "/mfa-setup",
        element: <MfaSetupPage />,
    },
    {
        path: "/oauth/consent",
        element: <OAuthConsentPage />,
    },
    {
        path: "/mfa-verify",
        element: <MfaVerificationPage />,
    },
    {
        path: "/",
        element: (
            <ProtectedRoute>
                <BaseLayout />
            </ProtectedRoute>
        ),
        children: [
            {
                index: true,
                element: <HomePage />,
            },
            {
                path: "home",
                element: <HomePage />,
            },
            {
                path: "tasks",
                element: <TaskPage />,
            },
            {
                path: "customers",
                element: <CustomerPage />,
            },
            // {
            // 	path: "voice",
            // 	element: (
            // 		<ComingSoon description="This dashboard will be the hub of all voice and sms communication between Aruza and their customers.  Centralizing communication will drastically improve efficiency with customers, their experience and impact the ability to see the whole customer picture" />
            // 	),
            // },
            {
                path: "communications",
                element: <CommunicationsPage />,
            },
            {
                path: "leads",
                element: <LeadsPage />,
            },
            {
                path: "commercial-leads/:leadId",
                element: <CommercialLeadViewerPage />,
            },
            {
                path: "commercial-leads/:leadId/contracts/new",
                element: <CommercialContractCreatePage />,
            },
            {
                path: "commercial-leads/:leadId/contracts/:contractId/edit",
                element: <CommercialContractCreatePage />,
            },
            {
                path: "commercial-leads/:leadId/contracts/:contractId/sent",
                element: <CommercialContractSentPage />,
            },
            {
                path: "dashboard",
                children: [
                    {
                        index: true,
                        element: <MarketingReporting />,
                    },
                    {
                        path: "leads",
                        element: <LeadsReportingPage />,
                    },
                    {
                        path: "doorknocking",
                        element: <DoorknockingDashboard />,
                    },
                    // Change userRole to BranchManager when the page is ready
                    {
                        path: "branch-manager",
                        element: <BranchManagerDashboard />,
                    },
                    {
                        path: "commercial",
                        element: <CommercialReportingDashboard />,
                    },
                    {
                        path: "stale-leads",
                        element: <StaleLeadsDashboard />,
                    },
                ],
            },
            // {
            // 	path: "reviews",
            // 	element: (
            // 		<ComingSoon description="This dashboard will serve as the central hub for managing and analyzing customer reviews. It will provide insights into customer feedback, help identify trends, and enable the team to respond promptly to improve customer satisfaction and business reputation." />
            // 	),
            // },
            // {
            // 	path: "super-map",
            // 	element: <SuperMapPage />,
            // },
            {
                path: "map-areas",
                element: <MapAreaPage />,
            },
            {
                path: "collections",
                element: <CollectionsPage />,
            },
            {
                path: "call-logs",
                element: (
                    <ProtectedRoute userRole={UserRoleEnum.User}>
                        <CallLogsPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "referrals",
                element: (
                    <ProtectedRoute userRole={UserRoleEnum.User}>
                        <ReferralPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "user-activity",
                element: (
                    <ProtectedRoute userRole={UserRoleEnum.BranchManager}>
                        <UserActivityPage />
                    </ProtectedRoute>
                ),
            },
            // {
            // 	path: "reports",
            // 	element: (
            // 		<ComingSoon description="This dashboard will be the central hub for generating and analyzing reports. It will provide insights into customer feedback, help identify trends, and enable the team to respond promptly to improve customer satisfaction and business reputation." />
            // 	),
            // },
            {
                path: "admin",
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedRoute>
                                <AdminPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: "bulk-create-fieldroutes-users",
                        element: (
                            <ProtectedRoute userRole={UserRoleEnum.Admin}>
                                <BulkFieldRoutesUsersPage />
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: "employee-contract/create",
                element: (
                    <ProtectedRoute>
                        <EmployeeContractCreatePage />
                    </ProtectedRoute>
                ),
            },
            // {
            //     path: "admin",
            //     element: (
            //         <ProtectedRoute userRole={UserRoleEnum.BranchManager}>
            //             <AdminPage />
            //         </ProtectedRoute>
            //     ),
            // },
            {
                path: "kpi-dashboard",
                element: (
                    <ProtectedRoute userRole={UserRoleEnum.Admin}>
                        <KPIDashboardPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "door-knocking-map",
                element: (
                    <ProtectedRoute>
                        <D2DLayout />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        element: <DoorKnockingMap />,
                    },
                ],
            },
            {
                path: "scheduling",
                element: (
                    <ProtectedRoute permission={UserPermissionsEnum.SchedulingView}>
                        <SchedulingPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "account",
                element: (
                    <ProtectedRoute>
                        <AccountPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: "help",
                element: <HelpPage />,
            },
            {
                // Splat (not ":slug") because doc slugs are multi-segment,
                // e.g. /help/billing/payments
                path: "help/*",
                element: <HelpPage />,
            },
            {
                // TODO: Review if this route should require authentication - work orders may contain sensitive data
                path: "work-order",
                element: <WorkOrderHtmlPage />,
            },
            // DEPRECATED: replaced by /proposal-html
            // {
            //     path: "proposal",
            //     element: <ProposalPDFPage />,
            // },
        ],
    },
    {
        path: "/d2d",
        element: (
            <ProtectedRoute>
                <D2DLayout />
            </ProtectedRoute>
        ),
        children: [
            {
                index: true,
                element: <DoorKnockingMap />,
            },
            {
                path: "scheduling",
                element: (
                    <div className="flex items-center justify-center h-screen">
                        <div className="max-w-screen-lg min-w-[1024px] border rounded p-4">
                            <TargetSaleScheduling />
                        </div>
                    </div>
                ),
            },
        ],
    },
    {
        path: "/contract-html",
        element: <ContractsPageHtml />,
    },
    {
        path: "work-order-html",
        element: <WorkOrderHtmlPage />,
    },
    {
        path: "/proposal-html",
        element: <ProposalHtmlPage />,
    },
    {
        path: "/commercial-contract",
        element: <CommercialContractPage />,
    },
    {
        path: "/commercial-proposal-html",
        element: <ContractsCommercialPageHtml />,
    },
    {
        path: "/forgot-password",
        element: <ForgotPasswordPage />,
    },
    {
        path: "/reset-password",
        element: <ResetPasswordPage />,
    },
    {
        path: "/web-form",
        element: <TestWebForms />,
    },
    {
        path: "/tech-lead-form",
        element: <TechLeadPage />,
    },
    {
        // TODO: Review if this route should require authentication - dynamic contracts may contain sensitive data
        path: "/dynamic-contract",
        element: <DynamicContractPage />,
    },
    {
        path: "/property-inspection",
        element: (
            <ProtectedRoute>
                <CompanyProvider>
                    <PropertyInspectionPage />
                </CompanyProvider>
            </ProtectedRoute>
        ),
    },
    {
        path: "/graph-editor",
        element: (
            <ProtectedRoute>
                <GraphingEditorPage />
            </ProtectedRoute>
        ),
    },
    {
        // TODO: Review if this route should require authentication - employee contracts may contain sensitive data
        path: "/employee-contract",
        element: <EmployeeContractPage />,
    },
    {
        path: "/referral",
        element: <CustomerReferralPage />,
    },
    {
        path: "/test-flow",
        element: <TestFlowPage />,
    },
].map((route) => ({
    ...route,
    errorElement: <ErrorBoundary />,
}));

// if (import.meta.env.MODE !== "production") {
//     routes = [{
//         path: "/",
//         element: <DevflagLayout />,
//         children: routes,
//         errorElement: <ErrorBoundary />,
//     }]
// }

// Wrap createBrowserRouter with Sentry for automatic navigation tracking
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
export const router = sentryCreateBrowserRouter(routes);
